import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

import GroupPageShell from "../components/common/GroupPageShell";
import DivisionClients from "../components/common/DivisionClients";
import DivisionClientsStrip from "../components/common/DivisionClientsStrip";
import Introduction from "../components/Hamdan Construction/ConstructIntroduction";
import Project from "../components/Hamdan Construction/ConstructProject";
import Contact from "../components/Hamdan Construction/ConstructContact";
import { buildClientLogoImages } from "../utils/clientLogos";
import DivisionServices from "../components/common/DivisionServices";

const HamdanConstruction: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { t, t_api } = useLanguage();


  useEffect(() => {
    fetch('/api/homepage/content')
      .then(res => res.json())
      .then(data => {
        setContent(data.Construction || {});
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  // Default tab is "about"
  const activeTab = searchParams.get("tab") || "about";

  // Menu items
  const menuItems = [
    { id: "about", label: t('common.about') },
    { id: "services", label: t('common.services') },
    { id: "project", label: t('common.projects') },
    { id: "clients", label: t('common.clients') },
    { id: "contact", label: t('common.contact') },
  ];

  if (loading) return <div className="min-h-screen flex items-center justify-center">{t('common.loading')}</div>;

  const clientLogoImages = buildClientLogoImages(content);
  const clientLogoUrls = t_api(content?.clients_logo_urls, content?.clients_logo_urls_ar);

  return (
    <GroupPageShell
      heroContent={{
        title: t_api(content?.banner_title, content?.banner_title_ar),
        subtitle: t_api(content?.banner_subtitle, content?.banner_subtitle_ar),
        banners: [1, 2, 3].map(i => ({
          image: content?.[`banner_${i}`] || ["/banner/banner4.jpg", "/banner/banner2.jpg", "/banner/banner4.jpg"][i - 1]
        }))
      }}
      tabs={menuItems}
      activeTab={activeTab}
      onTabChange={(tabId) => setSearchParams({ tab: tabId })}
    >
      <div className="mt-8">
        {activeTab === "about" && (
          <>
            <Introduction content={{
              kicker: t_api(content?.about_kicker, content?.about_kicker_ar),
              title: t_api(content?.about_title, content?.about_title_ar),
              desc: t_api(content?.about_desc, content?.about_desc_ar),
              introText: t_api(content?.about_intro_text, content?.about_intro_text_ar),
              introImage: content?.about_intro_image,
              imageBadgeTitle: t_api(content?.about_image_badge_title, content?.about_image_badge_title_ar),
              noteTitle: t_api(content?.about_note_title, content?.about_note_title_ar),
              noteDesc: t_api(content?.about_note_desc, content?.about_note_desc_ar),
              imageLabel: t_api(content?.about_image_label, content?.about_image_label_ar),
              panelTitle: t_api(content?.about_panel_title, content?.about_panel_title_ar),
              panelFooter: t_api(content?.about_panel_footer, content?.about_panel_footer_ar),
              capabilitiesTitle: t_api(content?.about_capabilities_title, content?.about_capabilities_title_ar),
              capabilitiesMeta: t_api(content?.about_capabilities_meta, content?.about_capabilities_meta_ar),
              brandTitle: t_api(content?.about_brand_title, content?.about_brand_title_ar),
              brandLogoAlt: t_api(content?.about_brand_logo_alt, content?.about_brand_logo_alt_ar),
              brandDesc: t_api(content?.about_brand_desc, content?.about_brand_desc_ar),
              brandTags: [1, 2, 3].map(i =>
                t_api(content?.[`about_brand_tag_${i}`], content?.[`about_brand_tag_${i}_ar`])
              ).filter(Boolean),
              points: [1, 2, 3].map(i => ({
                title: t_api(content?.[`about_pt_${i}_title`], content?.[`about_pt_${i}_title_ar`]),
                desc: t_api(content?.[`about_pt_${i}_desc`], content?.[`about_pt_${i}_desc_ar`]),
                icon: content?.[`about_pt_${i}_icon`]
              })).filter(p => p.title || p.desc)
            }} />
            <DivisionClientsStrip
              title={t_api(content?.clients_title, content?.clients_title_ar)}
              desc={t_api(content?.clients_desc, content?.clients_desc_ar)}
              clientsText={t_api(content?.clients_list, content?.clients_list_ar)}
              logoUrls={clientLogoUrls}
              logoImages={clientLogoImages}
            />
          </>
        )}
        {activeTab === "services" && (
          <DivisionServices content={content} />
        )}
        {activeTab === "project" && <Project content={{
          title: t_api(content?.proj_title, content?.proj_title_ar),
          images: [
            content?.banner_1 || "/banner/banner.jpg",
            content?.banner_2 || "/banner/banner2.jpg",
            content?.banner_3 || "/banner/banner4.jpg",
            content?.about_image || "/banner/banner5.jpg",
          ],
          projects: [1, 2, 3, 4, 5, 6].map(i => ({
            title: t_api(content?.[`proj_${i}_title`], content?.[`proj_${i}_title_ar`]),
            desc: t_api(content?.[`proj_${i}_desc`], content?.[`proj_${i}_desc_ar`]),
            category: t_api(content?.[`proj_${i}_cat`], content?.[`proj_${i}_cat_ar`]),
            status: t_api(content?.[`proj_${i}_status`], content?.[`proj_${i}_status_ar`]),
            image: content?.[`banner_${((i - 1) % 3) + 1}`] || content?.about_image,
            iconName: content?.[`proj_${i}_icon`]
          })).filter(p => p.title)
        }} />}
        {activeTab === "clients" && <DivisionClients content={{
          title: t_api(content?.clients_title, content?.clients_title_ar),
          desc: t_api(content?.clients_desc, content?.clients_desc_ar),
          clientsText: t_api(content?.clients_list, content?.clients_list_ar),
          logoUrls: clientLogoUrls,
          logoImages: clientLogoImages,
          divisionLabel: "Hamdan Construction",
          footer: t_api(content?.clients_footer, content?.clients_footer_ar),
          footerDesc: t_api(content?.clients_footer_desc, content?.clients_footer_desc_ar),
        }} />}
        {activeTab === "contact" && <Contact content={{
          title: t_api(content?.contact_form_title, content?.contact_form_title_ar),
          infoTitle: t_api(content?.contact_info_title, content?.contact_info_title_ar),
          infoDesc: t_api(content?.contact_info_desc, content?.contact_info_desc_ar),
          supportEmail: content?.contact_support_email,
          contactInfo: content?.contact_loc_name ? [{
            location: t_api(content?.contact_loc_name, content?.contact_loc_name_ar),
            address: t_api(content?.contact_loc_addr, content?.contact_loc_addr_ar),
            phone: content?.contact_loc_phone,
            fax: content?.contact_loc_fax
          }] : undefined
        }} />}
      </div>
    </GroupPageShell>
  );
};

export default HamdanConstruction;
